import { combine, date as coreDate, type DateValidatorOptions, required } from 'redux-form-validators';
import moment, { type Moment } from 'moment';
import i18n from 'i18next';
import * as fastIban from 'fast-iban';

const trimAndGetLength = (value: string): number => value.trim().length;

export const date = (options: DateValidatorOptions, initialValue?: Moment) => (value: Moment | undefined) => {
  if (!value) {
    return undefined;
  }

  if (moment(value).format('DD/MM/YYYY') === initialValue?.format('DD/MM/YYYY')) {
    return undefined;
  }
  return coreDate({ format: 'dd/mm/yyyy', ...options })(moment(value).format('DD/MM/YYYY'));
};

export const requiredDateAfter = (startDate?: string | Date) =>
  combine(...(startDate ? [date({ '>': new Date(startDate) })] : []), required());

export const asIban = (iban: string) => {
  try {
    const countryCode = iban.substring(0, 2);

    if (!isNaN(Number(countryCode)) && trimAndGetLength(iban) === 22) {
      return {
        suggestion: i18n.t('feedback.suggestions.the iban should be prefixed with', {
          ns: ['employee'],
          iban: iban.trim()
        })
      };
    }

    if (!isNaN(Number(countryCode))) {
      return {
        error: i18n.t('feedback.errors.iban must be spanish', {
          ns: ['employee']
        })
      };
    }
    if (trimAndGetLength(iban) < 21) {
      return {
        error: i18n.t('feedback.errors.the iban must contain at least 21 characters', { ns: ['employee'] })
      };
    }
    if (!fastIban.validateIBAN(iban)) {
      throw new Error(i18n.t('feedback.errors.iban is invalid', { ns: ['employee'] }) ?? '');
    }

    return false;
  } catch (e: any) {
    return {
      error: i18n.t('feedback.errors.iban is invalid', { ns: ['employee'] })
    };
  }
};

export const atLeastOneRequired = () => (value: string[] | undefined) =>
  value && value.length > 0 ? undefined : i18n.t('feedback.errors.select at least one', { ns: ['form'] });

export const ibanValidator = (value: string | undefined) => {
  if (!value) {
    return undefined;
  }

  const result = asIban(value);

  if (result && typeof result === 'object' && 'error' in result) {
    return result.error;
  }

  return undefined;
};
